import React, { useEffect, useState } from "react";
import { API } from "@/lib/constants";
import { Loader2, Upload, FileSpreadsheet, FileText, ShieldCheck, AlertTriangle, Check, History, ArrowLeftRight } from "lucide-react";

// ADMIN — Pricing sheet (Block Two item 12). Rates come from an uploaded sheet, READ by Rex,
// then CONFIRMED by a person before they are live. Same capture-and-confirm pattern as the
// interpreter, because these numbers reach clients: a model's reading must never go live unseen.
export default function PricingSheets() {
  const [card, setCard] = useState(null);
  const [history, setHistory] = useState([]);
  const [pending, setPending] = useState([]);
  const [review, setReview] = useState(null); // { sheet_id, extraction, questions, ... }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const j = (r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)));
  const loadAll = () => {
    fetch(`${API}/admin/pricing/rate-card`, { credentials: "include" }).then(j).then(setCard).catch((e) => setError(e.message));
    fetch(`${API}/admin/pricing/history`, { credentials: "include" }).then(j).then((d) => setHistory(d.versions || [])).catch(() => {});
    fetch(`${API}/admin/pricing/sheets/pending`, { credentials: "include" }).then(j).then((d) => setPending(d.pending || [])).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await fetch(`${API}/admin/pricing/sheets`, { method: "POST", credentials: "include", body: fd }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`); return r.json();
      });
      const ex = await fetch(`${API}/admin/pricing/sheets/${up.sheet_id}/extract`, { method: "POST", credentials: "include" }).then(j);
      setReview({ ...ex, version: "", effective_date: new Date().toISOString().slice(0, 10) });
      loadAll();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const openPending = async (id) => {
    setError(null);
    const s = await fetch(`${API}/admin/pricing/sheets/${id}`, { credentials: "include" }).then(j).catch(() => null);
    if (!s) return;
    let ex = s.extraction, questions = s.questions;
    if (!ex) { const r = await fetch(`${API}/admin/pricing/sheets/${id}/extract`, { method: "POST", credentials: "include" }).then(j); ex = r.extraction; questions = r.questions; }
    setReview({ sheet_id: id, filename: s.filename, extraction_mode: s.extraction_mode, extraction: ex, questions,
      version: "", effective_date: new Date().toISOString().slice(0, 10) });
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8" data-testid="pricing-sheets-page">
      <div className="mb-6 flex items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Pricing sheet</h1>
          <p className="mt-1 text-sm text-slate-500">Upload the rate card. Rex reads it, you confirm every figure, then it becomes the pricing we work from.</p>
        </div>
        {card && (
          <span className={`ml-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${card.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid="pricing-status">
            <ShieldCheck className="h-3.5 w-3.5" /> {card.active ? `Live: ${card.version}` : "No live pricing"}
          </span>
        )}
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="pricing-error">{error}</div>}

      {!review && (
        <>
          <label className="mb-6 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-white py-10 text-center transition-colors hover:border-slate-400" data-testid="pricing-upload-label">
            {busy ? <Loader2 className="h-6 w-6 animate-spin text-slate-400" /> : <Upload className="h-6 w-6 text-slate-400" />}
            <span className="mt-2 text-sm font-medium text-slate-700">{busy ? "Reading the sheet…" : "Upload a pricing sheet"}</span>
            <span className="mt-1 text-xs text-slate-400">.xlsx or .csv (read cell-exact) · .pdf or .docx (read from prose — every figure confirmed)</span>
            <input type="file" accept=".xlsx,.xls,.csv,.pdf,.docx" onChange={onUpload} className="hidden" data-testid="pricing-upload-input" disabled={busy} />
          </label>

          {pending.length > 0 && (
            <div className="mb-6" data-testid="pricing-pending">
              <h2 className="mb-2 text-sm font-semibold text-slate-700">Awaiting confirmation</h2>
              <div className="space-y-2">
                {pending.map((p) => (
                  <button key={p.sheet_id} onClick={() => openPending(p.sheet_id)} data-testid="pricing-pending-row"
                    className="flex w-full items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left hover:bg-amber-100">
                    {p.extraction_mode === "structured" ? <FileSpreadsheet className="h-4 w-4 text-amber-700" /> : <FileText className="h-4 w-4 text-amber-700" />}
                    <span className="flex-1 truncate text-sm text-slate-800">{p.filename}</span>
                    <span className="text-xs font-medium text-amber-700">Review →</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <ActiveCard card={card} />
          <VersionHistory versions={history} />
        </>
      )}

      {review && <Review review={review} setReview={setReview} onDone={() => { setReview(null); loadAll(); }} setError={setError} />}
    </div>
  );
}

function ActiveCard({ card }) {
  if (!card) return null;
  if (!card.active) return (
    <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400" data-testid="pricing-no-active">
      {card.reason}
    </div>
  );
  const rc = card.rate_card || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pricing-active-card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Live rate card</h2>
        <span className="text-xs text-slate-500">{card.version} · effective {card.effective_date} · confirmed by {card.confirmed_by}</span>
      </div>
      {["modules", "bundles", "modular_pricing"].map((b) => (rc[b]?.length ? (
        <div key={b} className="mb-3">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-400">{b.replace("_", " ")}</div>
          {rc[b].map((it, i) => (
            <div key={i} className="flex items-center justify-between border-b border-slate-100 py-1.5 text-sm">
              <span className="text-slate-700">{it.name}</span>
              <span className="font-medium text-slate-900">{it.amount != null ? `${rc.currency || ""} ${it.amount}` : "—"}{it.unit ? ` ${it.unit}` : ""}</span>
            </div>
          ))}
        </div>
      ) : null))}
    </div>
  );
}

function VersionHistory({ versions }) {
  if (!versions?.length) return null;
  return (
    <div className="mt-6" data-testid="pricing-history">
      <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700"><History className="h-4 w-4" /> Version history</h2>
      <div className="space-y-1.5">
        {versions.map((v) => (
          <div key={v.sheet_id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm" data-testid="pricing-history-row">
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${v.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{v.status}</span>
            <span className="font-medium text-slate-800">{v.version}</span>
            <span className="text-slate-500">effective {v.effective_date}</span>
            <span className="ml-auto truncate text-xs text-slate-400">{v.filename}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// The confirmation step — every extracted figure is shown, editable, with Rex's questions on top.
function Review({ review, setReview, onDone, setError }) {
  const [ex, setEx] = useState(review.extraction || {});
  const [saving, setSaving] = useState(false);
  const [diff, setDiff] = useState(null);
  const [ack, setAck] = useState(false);

  const loadDiff = React.useCallback((extraction) => {
    fetch(`${API}/admin/pricing/diff`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ extraction }),
    }).then((r) => (r.ok ? r.json() : null)).then((d) => { setDiff(d); setAck(false); }).catch(() => {});
  }, []);
  useEffect(() => { loadDiff(ex); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setAmount = (bucket, i, key, val) => {
    setEx((prev) => {
      const next = { ...prev, [bucket]: [...(prev[bucket] || [])] };
      next[bucket][i] = { ...next[bucket][i], [key]: key === "amount" ? (val === "" ? null : Number(val)) : val };
      return next;
    });
  };

  const moved = diff && diff.has_active && (diff.changed.length > 0 || diff.removed.length > 0);

  const confirm = async () => {
    if (!review.version.trim()) { setError("Give this rate card a version (e.g. v4)"); return; }
    if (moved && !ack) { setError("Review the changed rates and tick the box — a moved rate must not be waved through."); return; }
    setSaving(true); setError(null);
    try {
      await fetch(`${API}/admin/pricing/sheets/${review.sheet_id}/confirm`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ extraction: ex, version: review.version, effective_date: review.effective_date, acknowledged_changes: ack }),
      }).then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`); });
      onDone();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const questions = review.questions || [];
  return (
    <div data-testid="pricing-review">
      <div className="mb-4 flex items-center gap-2">
        <button onClick={onDone} className="text-sm text-slate-500 hover:text-slate-800" data-testid="pricing-review-back">← Back</button>
        <span className="ml-auto rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {review.extraction_mode === "structured" ? "Read cell-exact" : "Read from prose — confirm every figure"}
        </span>
      </div>

      {questions.length > 0 && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="pricing-questions">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Rex needs you to confirm</div>
          <ul className="space-y-1.5">
            {questions.map((q, i) => (
              <li key={i} className="text-sm text-amber-900" data-testid="pricing-question">
                <span className="rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium uppercase">{q.kind}</span> {q.question}
              </li>
            ))}
          </ul>
        </div>
      )}

      {diff && diff.has_active && (
        <div className="mb-5 rounded-xl border border-slate-300 bg-white p-4" data-testid="pricing-diff">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <ArrowLeftRight className="h-4 w-4" /> Changes from {diff.active_version}
            <button onClick={() => loadDiff(ex)} className="ml-auto text-xs font-normal text-slate-400 hover:text-slate-700" data-testid="pricing-diff-recheck">Re-check after edits</button>
          </div>
          {!moved && diff.added.length === 0
            ? <div className="text-sm text-slate-500" data-testid="pricing-diff-none">No rates changed from {diff.active_version}.</div>
            : (
              <div className="space-y-1.5 text-sm">
                {diff.changed.map((c, i) => (
                  <div key={`c${i}`} className="flex items-center gap-2 rounded bg-red-50 px-3 py-1.5" data-testid="pricing-diff-changed">
                    <span className="rounded bg-red-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-800">changed</span>
                    <span className="text-slate-700">{c.name}</span>
                    <span className="ml-auto"><span className="text-slate-400 line-through">{c.old_amount ?? "—"}{c.old_unit ? ` ${c.old_unit}` : ""}</span> <span className="mx-1 text-slate-400">→</span> <span className="font-semibold text-slate-900">{c.new_amount ?? "—"}{c.new_unit ? ` ${c.new_unit}` : ""}</span></span>
                  </div>
                ))}
                {diff.added.map((a, i) => (
                  <div key={`a${i}`} className="flex items-center gap-2 rounded bg-emerald-50 px-3 py-1.5" data-testid="pricing-diff-added">
                    <span className="rounded bg-emerald-200 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-800">new</span>
                    <span className="text-slate-700">{a.name}</span>
                    <span className="ml-auto font-semibold text-slate-900">{a.amount ?? "—"}{a.unit ? ` ${a.unit}` : ""}</span>
                  </div>
                ))}
                {diff.removed.map((r, i) => (
                  <div key={`r${i}`} className="flex items-center gap-2 rounded bg-slate-100 px-3 py-1.5" data-testid="pricing-diff-removed">
                    <span className="rounded bg-slate-300 px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-700">removed</span>
                    <span className="text-slate-500 line-through">{r.name}</span>
                    <span className="ml-auto text-slate-400">was {r.amount ?? "—"}</span>
                  </div>
                ))}
                <div className="pt-1 text-xs text-slate-400">{diff.unchanged} rate{diff.unchanged !== 1 ? "s" : ""} unchanged.</div>
              </div>
            )}
        </div>
      )}

      {["modules", "bundles", "modular_pricing"].map((bucket) => (
        <div key={bucket} className="mb-4">
          <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">{bucket.replace("_", " ")}</div>
          {(ex[bucket] || []).length === 0 && <div className="text-sm text-slate-400">None extracted.</div>}
          {(ex[bucket] || []).map((it, i) => (
            <div key={i} className="mb-1.5 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`pricing-item-${bucket}`}>
              <input value={it.name || ""} onChange={(e) => setAmount(bucket, i, "name", e.target.value)}
                className="flex-1 border-none text-sm text-slate-700 focus:outline-none" data-testid="pricing-item-name" />
              <input value={it.amount ?? ""} onChange={(e) => setAmount(bucket, i, "amount", e.target.value)} type="number" placeholder="amount"
                className="w-28 rounded border border-slate-300 px-2 py-1 text-right text-sm" data-testid="pricing-item-amount" />
              <input value={it.unit || ""} onChange={(e) => setAmount(bucket, i, "unit", e.target.value)} placeholder="unit"
                className="w-28 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="pricing-item-unit" />
            </div>
          ))}
        </div>
      ))}

      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm text-slate-600">Currency</label>
        <input value={ex.currency || ""} onChange={(e) => setEx((p) => ({ ...p, currency: e.target.value }))}
          placeholder="GBP" className="w-24 rounded border border-slate-300 px-2 py-1 text-sm" data-testid="pricing-currency" />
      </div>

      {moved && (
        <label className="mb-3 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-2.5 text-sm text-red-800" data-testid="pricing-ack">
          <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="pricing-ack-checkbox" />
          I have reviewed the {diff.changed.length + diff.removed.length} rate{diff.changed.length + diff.removed.length !== 1 ? "s" : ""} that moved and confirm they are correct.
        </label>
      )}

      <div className="flex items-end gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div>
          <label className="block text-xs font-medium text-slate-500">Version</label>
          <input value={review.version} onChange={(e) => setReview((r) => ({ ...r, version: e.target.value }))}
            placeholder="v4" className="mt-1 w-28 rounded border border-slate-300 px-2 py-1.5 text-sm" data-testid="pricing-version-input" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500">Effective date</label>
          <input type="date" value={review.effective_date} onChange={(e) => setReview((r) => ({ ...r, effective_date: e.target.value }))}
            className="mt-1 rounded border border-slate-300 px-2 py-1.5 text-sm" data-testid="pricing-effective-input" />
        </div>
        <button onClick={confirm} disabled={saving || (moved && !ack)} data-testid="pricing-confirm-btn"
          className="ml-auto flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm & make live
        </button>
      </div>
    </div>
  );
}
